import React, {useEffect, useRef, useState} from 'react';
import {
  Route,
  Switch,
  BrowserRouter as Router,
} from 'react-router-dom';
import {TabAccessibilityController} from './components/TabAccessibilityController';
import {Theme} from './utils/theme';
import {PlayerContext} from './modules/Editor/PlayerContext';
import {breakpoints, getBreakpointMedia} from './utils/facepaint';
import {loadableRetry} from '@snapper/utils';

const Editor = loadableRetry(() => {
  console.log('load Editor');

  return import(/* webpackChunkName: "Editor" */ './Editor');
}, {name: 'Editor'});

const PlayerContainer = loadableRetry(() => {
  console.log('load Player');

  return import(/*  webpackPrefetch: true, webpackChunkName: "Player" */ './modules/Player');
}, {name: 'Player'});

const ScormWrapper = loadableRetry(() => {
  console.log('load ScormWrapper');

  return import(/* webpackChunkName: "ScormWrapper" */ './modules/Scorm/wrapper');
}, {name: 'ScormWrapper'});

const Component = process.env.REACT_APP_TYPE === 'editor'
  ? Editor
  : process.env.REACT_APP_TYPE === 'scorm'
    ? ScormWrapper
    : PlayerContainer;

export const App = () => {
  useEffect(() => {
    const version = process.env.REACT_APP_VERSION; // is set on build to match package.json version
    const buildHash = process.env.REACT_APP_GIT_SHA; // this is set to match sha1 of git HEAD

    if (buildHash || version) {
      // eslint-disable-next-line no-console
      console.log(`${buildHash?.trim()} v. ${version}\n${window.navigator.userAgent}`);
    }
  }, []);

  const [_navButtonColors, setNavigationButtonColors] = useState(['#fff']);
  const navigationButtonColor = !!_navButtonColors?.length && _navButtonColors[_navButtonColors.length - 1] || '#fff';
  const scrollRef = useRef(null);
  const [currentSlideStarted, setCurrentSlideStarted] = useState(true);
  const [scrollIndicator, setScrollIndicator] = useState(false);
  const [nextNavigationDisabled, setNextNavigationDisabled] = useState(false);
  const [mqSize, setMqSize] = useState('large');
  const [previewMode, setPreviewMode] = useState(window.previewMode || false);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState(null);

  window.setPreviewMode = setPreviewMode;
  window.previewMode = previewMode;
  window.loading = loading;

  useEffect(() => {
    const mediaQueries = Object.entries(breakpoints).map(([key, media]) => ({
      media: window.matchMedia(getBreakpointMedia(media)),
      onChange: ({matches}) => matches && setMqSize(key),
    }));

    mediaQueries.forEach(({media, onChange}) => {
      if (media.matches) {
        onChange({matches: true});
      }
      if (media.addEventListener) {
        media.addEventListener('change', onChange);
      } else {
        media.addListener(onChange);
      }
    });

    return () => {
      mediaQueries.forEach(({media, onChange}) => {
        if (media.removeEventListener) {
          media.removeEventListener('change', onChange);
        } else {
          media.removeListener(onChange);
        }
      });
    };
  }, []);

  return (
    <TabAccessibilityController>
      <Theme>
        <PlayerContext.Provider
          value={{
            mqSize,
            currentSlideStarted,
            setCurrentSlideStarted,
            scrollIndicator,
            nextNavigationDisabled,
            setNextNavigationDisabled,
            setScrollIndicator,
            scrollRef,
            images,
            setImages,
            navigationButtonColor,
            setNavigationButtonColors,
            fullscreen: previewMode,
            skipAnimations: previewMode,
            loading,
            setLoading,
          }}
        >
          <Router>
            <Switch>
              {process.env.REACT_APP_TYPE
                ? (
                  <Route
                    path="*" 
                    component={Component}
                  />
                ) 
                : (
                  <>
                    <Route
                      path="/editor"
                      component={Editor}
                    />
                    <Route
                      path="/player/:id?/:token?"
                      component={PlayerContainer}
                    />
                  </>
                )}
            </Switch>
          </Router>
        </PlayerContext.Provider>
      </Theme>
    </TabAccessibilityController>
  );
};
